import img from 'app/assets/icons/attention.svg';
import useInputFontSizeClass from 'app/hooks/useInputFontSizeClass';
import Input, { InputConfig } from 'app/components/form/Input';

import { FormItemInput, InputTypeHint } from '../types';
import { useContactFormLinkClick } from '../hooks/useContactFormLinkClick';

type InputType = 'text' | 'number' | 'email' | 'tel' | 'password' | 'url' | 'search' | 'textarea';

// Map contact form input types to generic input types
const inputTypeMapping: Record<InputTypeHint, InputType> = {
  text: 'text',
  number: 'number',
  email: 'email',
  phone: 'tel',
  'zip-code': 'text',
  'email-or-phone': 'text',
  'npi-number': 'number',
  multi_line_text: 'textarea',
};

// Custom configurations for specific input types
const customConfigs: Record<InputTypeHint, Partial<InputConfig>> = {
  text: {
    type: 'text',
    maxLength: 100,
    inputMode: 'text',
    autoComplete: 'on',
  },
  number: {
    type: 'number',
    inputMode: 'numeric',
  },
  email: {
    type: 'email',
    maxLength: 320,
    inputMode: 'email',
    autoComplete: 'on',
  },
  phone: {
    type: 'tel',
    maxLength: 20,
    inputMode: 'tel',
    autoComplete: 'on',
  },
  'zip-code': {
    type: 'text',
    pattern: '\\d{5}(?:[\\-\\s]\\d{4})?',
    inputMode: 'numeric',
    autoComplete: 'on',
  },
  'email-or-phone': {
    type: 'text',
  },
  'npi-number': {
    type: 'number',
    inputMode: 'numeric',
    autoComplete: 'on',
  },
  multi_line_text: {
    type: 'text',
    maxLength: 150,
  },
};

type Props = {
  item: FormItemInput;
  isSubmitted: boolean;
  isDirty: boolean;
  onChange: (value: string) => void;
  contactFormSlug?: string;
};

export function ContactFormInput({ item, isSubmitted, isDirty, onChange, contactFormSlug }: Props) {
  const { handleLinkClick } = useContactFormLinkClick(contactFormSlug);
  const fontSizeClass = useInputFontSizeClass();
  const hasErrors = item.errors?.length;
  const showError = isSubmitted && !!hasErrors && !isDirty;
  const errorId = `contact-form-input-error-${item.ref_id}`;

  const inputType = inputTypeMapping[item.input_type_hint];
  const customConfig = customConfigs[item.input_type_hint];

  return (
    <Input
      className="ostro-contact-form-input"
      id={item.ref_id}
      name={item.ref_id}
      label={item.label}
      value={(item.value ?? '') as string}
      placeholder={item.placeholder}
      required={item.required}
      error={item.errors}
      showError={showError}
      onChange={onChange}
      customConfig={customConfig}
      inputClassName={`hide-number-input-arrows ${fontSizeClass}`}
      minHeight={inputType === 'textarea' ? '60px' : undefined}
      style={{
        backgroundPosition: showError ? 'right 14px center' : undefined,
        backgroundImage: showError ? `url(${img})` : undefined,
        backgroundRepeat: showError ? 'no-repeat' : undefined,
      }}
      onLabelClick={handleLinkClick}
      errorId={errorId}
    />
  );
}
